import{useQuery}from'@tanstack/react-query';import{useDataCentralService}from'../../../../services/data-central/data-central-service.provider';/**
 * Shared hook for querying dashboard views with React Query caching
 * Automatically handles loading, error states, and caching (5 min stale time)
 */export function useDashboardQuery(queryKey,sql,options){var _options$staleTime,_options$refetchOnWin,_options$enabled;const dataCentral=useDataCentralService();return useQuery({queryKey,queryFn:()=>dataCentral.executeQuery(sql),staleTime:(_options$staleTime=options===null||options===void 0?void 0:options.staleTime)!==null&&_options$staleTime!==void 0?_options$staleTime:5*60*1000,// 5 minutes default
refetchOnWindowFocus:(_options$refetchOnWin=options===null||options===void 0?void 0:options.refetchOnWindowFocus)!==null&&_options$refetchOnWin!==void 0?_options$refetchOnWin:false,enabled:(_options$enabled=options===null||options===void 0?void 0:options.enabled)!==null&&_options$enabled!==void 0?_options$enabled:true});}/**
 * Hook for querying executive overview data
 */export function useExecutiveOverview(){return useDashboardQuery(['executive-overview'],`
      SELECT * FROM \`phydatacentral.vw_sales_executive_overview\`
      ORDER BY monthDate DESC
      LIMIT 24
    `);}/**
 * Hook for querying top growing/declining tenants
 */export function useTopTenants(){const topGrowingByPct=useDashboardQuery(['top-growing-pct'],`
      SELECT
        tenantId,
        tenantName,
        country,
        mrrGrossUsd,
        mrrGrossOriginal,
        originalCurrency,
        momGrowthGrossPct,
        momGrowthGrossAbsUsd,
        yoyGrowthGrossPct
      FROM \`phydatacentral.vw_sales_monthly_metrics\`
      WHERE monthDate = (SELECT MAX(monthDate) FROM \`phydatacentral.vw_sales_monthly_metrics\` WHERE isDemo = false)
        AND isDemo = false
        AND momGrowthGrossPct IS NOT NULL
      ORDER BY momGrowthGrossPct DESC
      LIMIT 5
    `);const topGrowingByDollar=useDashboardQuery(['top-growing-dollar'],`
      SELECT
        tenantId,
        tenantName,
        country,
        mrrGrossUsd,
        mrrGrossOriginal,
        originalCurrency,
        momGrowthGrossPct,
        momGrowthGrossAbsUsd,
        yoyGrowthGrossPct
      FROM \`phydatacentral.vw_sales_monthly_metrics\`
      WHERE monthDate = (SELECT MAX(monthDate) FROM \`phydatacentral.vw_sales_monthly_metrics\` WHERE isDemo = false)
        AND isDemo = false
        AND momGrowthGrossAbsUsd IS NOT NULL
      ORDER BY momGrowthGrossAbsUsd DESC
      LIMIT 5
    `);const topDecliningByPct=useDashboardQuery(['top-declining-pct'],`
      SELECT
        tenantId,
        tenantName,
        country,
        mrrGrossUsd,
        mrrGrossOriginal,
        originalCurrency,
        momGrowthGrossPct,
        momGrowthGrossAbsUsd,
        yoyGrowthGrossPct
      FROM \`phydatacentral.vw_sales_monthly_metrics\`
      WHERE monthDate = (SELECT MAX(monthDate) FROM \`phydatacentral.vw_sales_monthly_metrics\` WHERE isDemo = false)
        AND isDemo = false
        AND momGrowthGrossPct IS NOT NULL
      ORDER BY momGrowthGrossPct ASC
      LIMIT 5
    `);const topDecliningByDollar=useDashboardQuery(['top-declining-dollar'],`
      SELECT
        tenantId,
        tenantName,
        country,
        mrrGrossUsd,
        mrrGrossOriginal,
        originalCurrency,
        momGrowthGrossPct,
        momGrowthGrossAbsUsd,
        yoyGrowthGrossPct
      FROM \`phydatacentral.vw_sales_monthly_metrics\`
      WHERE monthDate = (SELECT MAX(monthDate) FROM \`phydatacentral.vw_sales_monthly_metrics\` WHERE isDemo = false)
        AND isDemo = false
        AND momGrowthGrossAbsUsd IS NOT NULL
      ORDER BY momGrowthGrossAbsUsd ASC
      LIMIT 5
    `);return{topGrowingByPct,topGrowingByDollar,topDecliningByPct,topDecliningByDollar};}/**
 * Hook for querying customer list
 */export function useCustomers(){return useDashboardQuery(['customers'],`
      SELECT *
      FROM \`phydatacentral.vw_sales_customers\`
      WHERE isDemo = false
      ORDER BY mrrGrossUsd DESC
    `);}/**
 * Hook for querying product list
 */export function useProducts(){return useDashboardQuery(['products'],`
      SELECT *
      FROM \`phydatacentral.vw_sales_products\`
      WHERE monthDate = (SELECT MAX(monthDate) FROM \`phydatacentral.vw_sales_products\`)
        AND isDemo = false
      ORDER BY totalRevenueUsd DESC
    `);}/**
 * Hook for querying operations issues
 */export function useOperationsIssues(){return useDashboardQuery(['operations-issues'],`
      SELECT *
      FROM \`phydatacentral.vw_sales_operations_issues\`
      WHERE isDemo = false
      ORDER BY issueType, tenantId
    `);}/**
 * Hook for querying usage data
 */export function useUsageData(){return useDashboardQuery(['usage'],`
      SELECT *
      FROM \`phydatacentral.vw_sales_usage\`
      WHERE isDemo = false
      ORDER BY engagementScore DESC
    `);}/**
 * Hook for querying trends data
 */export function useTrendsData(){return useDashboardQuery(['trends'],`
      SELECT *
      FROM \`phydatacentral.vw_sales_trends_monthly\`
      WHERE isDemo = false
      ORDER BY tenantId, monthDate DESC
    `);}/**
 * Hook for querying tenant drilldown (12 months)
 */export function useTenantDrilldown(tenantId){return useDashboardQuery(['tenant-drilldown',tenantId],`
      SELECT *
      FROM \`phydatacentral.vw_sales_tenant_drilldown\`
      WHERE tenantId = '${tenantId}'
      ORDER BY monthDate DESC
    `,{enabled:!!tenantId});}/**
 * Hook for querying SKU drilldown (12 months + customer list)
 */export function useSkuDrilldown(sku){return useDashboardQuery(['sku-drilldown',sku],`
      SELECT *
      FROM \`phydatacentral.vw_sales_sku_drilldown\`
      WHERE sku = '${sku}'
        AND isDemo = false
      ORDER BY monthDate DESC, revenueUsd DESC
    `,{enabled:!!sku});}/**
 * Hook for querying SKU summary from products view
 */export function useSkuSummary(sku){return useDashboardQuery(['sku-summary',sku],`
      SELECT *
      FROM \`phydatacentral.vw_sales_products\`
      WHERE sku = '${sku}'
        AND isDemo = false
      ORDER BY monthDate DESC
      LIMIT 12
    `,{enabled:!!sku});}/**
 * Hook for querying SKU pricing information
 */export function useSkuPricing(sku){return useDashboardQuery(['sku-pricing',sku],`
      SELECT
        sku,
        name,
        unit,
        priceInformation
      FROM \`phydatacentral.sku\`
      WHERE sku = '${sku}'
      LIMIT 1
    `,{enabled:!!sku});}